import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
  RowActionsMenu,
} from '@carboledger/ui';
import { MoreHorizontal } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { LoadingOverlay, EmptyState, ConfirmDialog } from '@/components/feedback';
import { DiscrepancyModal, DetailPageHeader } from '@/components/common';
// Update shipment IS the outgoing-shipment form, so it uses that slice's component rather than a copy.
import { OutgoingShipmentForm } from '@/slices/outgoing-shipments/components/OutgoingShipmentForm';
import { useGetOrderDetailQuery, useDeleteOrderMutation } from '../api/ordersApi';
import { ORDER_DISPLAY_STATUS } from '../constants/orderDisplayStatus';
import { ORDER_DISPLAY_STATUS_TONE } from '../constants/orderDisplayStatusTone';
import { buildOrderDiscrepancySections } from '../constants/orderDiscrepancy';
import { ORDER_ACTIONS } from '../constants/orderActions';
import { OrderOverviewTabView } from '../components/OrderOverviewTabView';
import { LinkedOutgoingShipmentTabView } from '../components/LinkedOutgoingShipmentTabView';
import { AllocationDetailsTabView } from '../components/AllocationDetailsTabView';
import { useOrderUpdateShipment } from '../hooks';
import styles from './OrderDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  shipments: 'shipments',
  allocations: 'allocations',
});

// Outbounds ▸ Orders ▸ Order details.
export const OrderDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, orderId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);
  const [isUpdateShipmentOpen, setIsUpdateShipmentOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteOrder] = useDeleteOrderMutation();

  const {
    data: order,
    isLoading: isLoadingOrder,
    isError: isOrderError,
  } = useGetOrderDetailQuery({ id: orderId });

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('SECTION_OVERVIEW') },
      { value: DetailTab.shipments, label: t('ORDERS_DETAIL_TAB_SHIPMENTS') },
      { value: DetailTab.allocations, label: t('ORDERS_DETAIL_TAB_ALLOCATIONS') },
    ],
    [t],
  );

  const goToOrders = useCallback(() => navigate(ROUTES.orders(module)), [navigate, module]);

  const breadcrumbItems = useMemo(
    () => [
      { label: t('ORDERS_TITLE'), onClick: () => navigate(ROUTES.orders(module)) },
      { label: order?.orderNo ?? '-' },
    ],
    [t, navigate, module, order?.orderNo],
  );

  // lockedFromOrder's own read-only text — the names behind orderForShipment's ids.
  const lockedShipmentDisplayValues = useMemo(
    () => ({
      facility: order?.facilityName,
      material: order?.materialName,
      soldTo: order?.soldTo,
      shippingTo: order?.shippingTo,
    }),
    [order?.facilityName, order?.materialName, order?.soldTo, order?.shippingTo],
  );

  const handleOpenDiscrepancies = useCallback(() => setIsDiscrepancyOpen(true), []);
  const handleCloseDiscrepancies = useCallback(() => setIsDiscrepancyOpen(false), []);

  const handleUpdateShipment = useCallback(() => setIsUpdateShipmentOpen(true), []);
  const handleCloseUpdateShipment = useCallback(() => setIsUpdateShipmentOpen(false), []);

  // Fetched only once the modal is asked for, so the detail page's own load stays cheap.
  const updateShipment = useOrderUpdateShipment({
    module,
    orderId,
    open: isUpdateShipmentOpen,
    onClose: handleCloseUpdateShipment,
  });
  // The order's own names, plus a free-text terminal the form must keep.
  const shipmentDisplayValues = useMemo(
    () => ({ ...lockedShipmentDisplayValues, terminal: updateShipment.legacyTerminal }),
    [lockedShipmentDisplayValues, updateShipment.legacyTerminal],
  );

  const handleFulfill = useCallback(() => {
    navigate(ROUTES.orderFulfill(module, orderId));
  }, [navigate, module, orderId]);

  const handleEdit = useCallback(
    () => navigate(ROUTES.orderEdit(module, orderId)),
    [navigate, module, orderId],
  );

  const handleOpenDelete = useCallback(() => setIsDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setIsDeleteOpen(false), []);

  // The order is gone once this resolves, so the detail route has nothing left to render.
  const handleConfirmDelete = useCallback(async () => {
    try {
      await deleteOrder({ module, id: orderId }).unwrap();
      setIsDeleteOpen(false);
      goToOrders();
    } catch {
      // notifyApiErrors already surfaced it.
    }
  }, [deleteOrder, module, orderId, goToOrders]);

  const menuItems = useMemo(
    () => [
      { id: 'edit', label: t('COMMON_EDIT'), onSelect: handleEdit },
      {
        id: 'delete',
        label: t('COMMON_DELETE'),
        disabled: ORDER_ACTIONS.delete.disabled(order),
        onSelect: handleOpenDelete,
      },
    ],
    [t, handleEdit, handleOpenDelete, order],
  );

  const canUpdateShipment =
    order?.statusCode === ORDER_DISPLAY_STATUS.active ||
    order?.statusCode === ORDER_DISPLAY_STATUS.closed;

  const metaFields = useMemo(() => {
    if (!order) return [];
    return [
      { id: 'material', label: t('FIELD_MATERIAL_NAME'), value: order.materialName },
      { id: 'facility', label: t('FIELD_FACILITY_NAME'), value: order.facilityName },
      { id: 'terminal', label: t('FIELD_TERMINAL'), value: order.terminal },
      {
        id: 'status',
        label: t('FIELD_STATUS'),
        value: order.statusLabel,
        type: 'status',
        tone: ORDER_DISPLAY_STATUS_TONE[order.statusCode] ?? BadgeVariants.default,
      },
      { id: 'soldTo', label: t('FIELD_SOLD_TO'), value: order.soldTo },
      { id: 'shippingTo', label: t('FIELD_SHIPPING_TO'), value: order.shippingTo },
      { id: 'dispatchBy', label: t('FIELD_DISPATCH_BY'), value: order.dispatchBy },
      {
        id: 'discrepancies',
        label: t('FIELD_DISCREPANCIES'),
        count: order.discrepancyCount,
        type: 'discrepancy',
        ariaLabel: t('ACTION_VIEW_DISCREPANCIES'),
        onClick: handleOpenDiscrepancies,
      },
    ];
  }, [t, order, handleOpenDiscrepancies]);

  const discrepancySections = useMemo(() => buildOrderDiscrepancySections(t, order), [t, order]);

  // computeOrderDiscrepancies (iscc-backend) returns a 0|1 flag, not itemized discrepancies — one fixed message stands in for the missing detail list.
  const discrepancyMessage = order?.discrepancyCount
    ? t('ORDERS_DETAIL_DISCREPANCY_MESSAGE')
    : undefined;

  if (isLoadingOrder) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isOrderError || !order) {
    return (
      <EmptyState
        title={t('ORDERS_DETAIL_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToOrders}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={order.orderNo}
        actions={
          <>
            {canUpdateShipment ? (
              <Button
                variant={ButtonVariants.secondary}
                size={ButtonSizes.sm}
                onClick={handleUpdateShipment}
                loading={isUpdateShipmentOpen && updateShipment.isLoading}
              >
                {t('ACTION_UPDATE_SHIPMENT')}
              </Button>
            ) : null}
            <Button variant={ButtonVariants.primary} size={ButtonSizes.sm} onClick={handleFulfill}>
              {t('ACTION_FULFILL')}
            </Button>
            <RowActionsMenu
              items={menuItems}
              ariaLabel={t('COMMON_MORE_ACTIONS')}
              icon={MoreHorizontal}
            />
          </>
        }
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('ORDERS_TITLE')}
      />

      <div className={styles.content}>
        {activeTab === DetailTab.overview && <OrderOverviewTabView order={order} />}

        {activeTab === DetailTab.shipments && (
          <LinkedOutgoingShipmentTabView module={module} order={order} />
        )}

        {activeTab === DetailTab.allocations && (
          <AllocationDetailsTabView
            module={module}
            orderId={orderId}
            allocationsSummary={order.allocationsSummary}
            allocatedGhgSavings={order.allocatedGhgSavings}
          />
        )}
      </div>

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('ORDERS_DELETE_TITLE')}
        bodyTitle={t('ORDERS_DELETE_BODY_TITLE', { orderNo: order.orderNo })}
        description={t('ORDERS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <DiscrepancyModal
        open={isDiscrepancyOpen}
        onClose={handleCloseDiscrepancies}
        referenceId={order.orderNo}
        message={discrepancyMessage}
        sections={discrepancySections}
      />

      {updateShipment.ready ? (
        <OutgoingShipmentForm
          asModal
          open={isUpdateShipmentOpen}
          title={t('ORDERS_UPDATE_SHIPMENT_TITLE', { orderNo: order.orderNo })}
          description={t('ORDERS_UPDATE_SHIPMENT_DESCRIPTION')}
          submitLabel={t('COMMON_SAVE')}
          initialValues={updateShipment.initialValues}
          onSubmit={updateShipment.onSubmit}
          onClose={handleCloseUpdateShipment}
          isSubmitting={updateShipment.isSubmitting}
          options={updateShipment.options}
          lockedFromOrder
          lockedDisplayValues={shipmentDisplayValues}
        />
      ) : null}
    </div>
  );
};
